import type { TaskStatus } from "./tasksConstants";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function startOfLocalToday(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/** Monday-start week at local midnight. */
export function startOfLocalWeek(d: Date = new Date()): Date {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const jsDay = base.getDay(); // 0=Sun..6=Sat
  const mondayIndex = (jsDay + 6) % 7; // Mon=0..Sun=6
  base.setDate(base.getDate() - mondayIndex);
  return base;
}

export function startOfLocalMonth(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endExclusiveFromStart(start: Date, period: "week" | "month"): Date {
  const end = new Date(start);
  if (period === "week") end.setDate(end.getDate() + 7);
  else end.setMonth(end.getMonth() + 1);
  return end;
}

export function isWithinRange(dateIso: string, start: Date, endExclusive: Date): boolean {
  const t = new Date(dateIso).getTime();
  return Number.isFinite(t) && t >= start.getTime() && t < endExclusive.getTime();
}

/** Parse Postgres `date` as local midnight (`YYYY-MM-DD`). */
export function parseTaskDueDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return new Date(NaN);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Days from local today to due date (negative = overdue). */
export function calendarDaysUntilDue(dueDateIso: string | null | undefined): number | null {
  if (!dueDateIso) return null;
  const due = parseTaskDueDate(dueDateIso);
  if (Number.isNaN(due.getTime())) return null;
  const today = startOfLocalToday();
  return Math.round((due.getTime() - today.getTime()) / MS_PER_DAY);
}

/** Active tasks with a due date that is overdue or within the next `withinDays` calendar days (inclusive). */
export function isTaskDeadlineUrgent(
  dueDate: string | null | undefined,
  status: TaskStatus,
  withinDays = 5
): boolean {
  if (status === "done" || status === "cancelled") return false;
  const days = calendarDaysUntilDue(dueDate ?? null);
  if (days === null) return false;
  return days < 0 || days <= withinDays;
}

export function formatDeadlineBadge(days: number): string {
  if (days < 0) {
    const n = Math.abs(days);
    return n === 1 ? "1 day overdue" : `${n} days overdue`;
  }
  if (days === 0) return "Due today";
  if (days === 1) return "1 day left";
  return `${days} days left`;
}

/** Short label for tight list rows. */
export function formatDeadlineBadgeShort(days: number): string {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  return `${days}d left`;
}

export function formatCreatorLabel(email: string | null | undefined): string | null {
  if (!email?.trim()) return null;
  const e = email.trim();
  const at = e.indexOf("@");
  if (at > 0) return e.slice(0, at);
  return e;
}

/** Compact, non-localized relative time, e.g. "just now", "5m ago", "3h ago", "2d ago". */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diffMs = now.getTime() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 30) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.round(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
}

export function formatAbsoluteDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Display: T001…T999, then T1000+ */
export function formatTaskCode(taskNumber: number): string {
  if (taskNumber <= 999) return `T${String(taskNumber).padStart(3, "0")}`;
  return `T${taskNumber}`;
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Route param may be UUID, T042 / t042, or plain digits like 042 / 42. */
export function parseTaskRouteParam(raw: string): { by: "id"; id: string } | { by: "task_number"; n: number } | null {
  const s = raw.trim();
  if (!s) return null;
  if (UUID_REGEX.test(s)) return { by: "id", id: s };

  const tDigits = /^t(\d{1,10})$/i.exec(s);
  if (tDigits) {
    const n = Number.parseInt(tDigits[1], 10);
    if (Number.isFinite(n) && n > 0) return { by: "task_number", n };
    return null;
  }

  if (/^\d{1,10}$/.test(s)) {
    const n = Number.parseInt(s, 10);
    if (Number.isFinite(n) && n > 0) return { by: "task_number", n };
    return null;
  }

  return null;
}

export function statusDotClass(status: TaskStatus) {
  switch (status) {
    case "to-do":
      return "bg-slate-400";
    case "progress":
      return "bg-sky-500";
    case "done":
      return "bg-emerald-500";
    case "cancelled":
      return "bg-red-500";
    default:
      return "bg-slate-400";
  }
}

